/* =========================================================
   INTELLICLEAR PRIVATE PRICING
   Premium 2-stage request flow

   Includes:
   - True viewport modal portal
   - Step 1 / Step 2
   - Selection rules
   - Variant thumbnails
   - Shopify contact form
   - Instant private checkout for clear selections
     (/apps/intelliclear-pricing/checkout). Clear selections
     never submit the contact form; "I'm not sure" does.
   - Clear selections continue to the private purchase page
     (/pages/private-purchase) when the theme has it; the
     checkout-ready screen remains the fallback.
   - Private-pricing access: /checkout grants a 30-day token,
     kept in localStorage (app proxies strip cookies) and sent
     only in POST bodies. Returning visitors with valid access
     skip the request form.
   - Personalized success receipt
   - Success-state thumbnails
   - Responsive process experience
   ========================================================= */

(function () {
  'use strict';


  function initIntelliClearPricing(root) {
    if (!root) return;

    if (root.dataset.icPricingInitialized === 'true') {
      return;
    }

    root.dataset.icPricingInitialized = 'true';


    const openButton =
      root.querySelector('[data-ic-pricing-open]');

    let modal =
      root.querySelector('[data-ic-pricing-modal]');


    if (!openButton || !modal) {
      return;
    }


    /* =====================================================
       PORTAL MODAL TO BODY
       ===================================================== */

    const modalId =
      modal.id;


    if (modalId) {
      const duplicates =
        Array.from(
          document.body.querySelectorAll(
            '[data-ic-pricing-modal]'
          )
        ).filter(function (item) {
          return (
            item.id === modalId &&
            item !== modal
          );
        });


      duplicates.forEach(function (item) {
        item.remove();
      });
    }


    document.body.appendChild(modal);



    /* =====================================================
       DOM
       ===================================================== */

    const modalBody =
      modal.querySelector('[data-ic-modal-body]');


    const closeButtons =
      modal.querySelectorAll('[data-ic-pricing-close]');


    const backdrop =
      modal.querySelector('.ic-pricing-modal__backdrop');


    const step1 =
      modal.querySelector('[data-ic-step-panel="1"]');


    const step2 =
      modal.querySelector('[data-ic-step-panel="2"]');


    const nextButton =
      modal.querySelector('[data-ic-next]');


    const submitButton =
      modal.querySelector('[data-ic-submit]');


    const backButton =
      modal.querySelector('[data-ic-back]');


    const changeButton =
      modal.querySelector('[data-ic-change-selection]');


    const progressFill =
      modal.querySelector('[data-ic-progress-fill]');


    const stepCurrent =
      modal.querySelector('[data-ic-step-current]');


    const optionInputs =
      Array.from(
        modal.querySelectorAll('[data-ic-request-option]')
      );


    const requestedItemsField =
      modal.querySelector('[data-ic-requested-items]');


    const requestedVariantIdsField =
      modal.querySelector('[data-ic-requested-variant-ids]');


    const selectionError =
      modal.querySelector('[data-ic-selection-error]');


    const summaryImage =
      modal.querySelector('[data-ic-summary-image]');


    const summaryText =
      modal.querySelector('[data-ic-summary-text]');


    const form =
      modal.querySelector('form');


    const firstNameInput =
      modal.querySelector(
        'input[name="contact[first_name]"]'
      );


    const emailInput =
      modal.querySelector(
        'input[name="contact[email]"]'
      );


    const successState =
      modal.querySelector('[data-ic-success]');


    const formErrorState =
      modal.querySelector(
        '.ic-pricing-modal__form-errors'
      );


    const phoneInput =
      modal.querySelector(
        'input[name="contact[phone]"]'
      );


    const honeypotInput =
      modal.querySelector('[data-ic-honeypot]');


    const zipInput =
      modal.querySelector('[data-ic-zip]');


    /* Step 2 copy: instant access for clear selections, honest
       follow-up wording for "I'm not sure what I need". */
    const instantCopy =
      Array.from(modal.querySelectorAll('[data-ic-copy-instant]'));


    const manualCopy =
      Array.from(modal.querySelectorAll('[data-ic-copy-manual]'));


    const checkoutReady =
      modal.querySelector('[data-ic-checkout-ready]');


    const checkoutReadyTitle =
      modal.querySelector('[data-ic-checkout-ready-title]');


    const checkoutReadyItems =
      modal.querySelector('[data-ic-checkout-ready-items]');


    const checkoutContinueButton =
      modal.querySelector('[data-ic-checkout-continue]');


    const checkoutError =
      modal.querySelector('[data-ic-checkout-error]');


    const progressRow =
      modal.querySelector('.ic-pricing-modal__progress-row');


    const unlockedState =
      modal.querySelector('[data-ic-unlocked]');


    const unlockedTitle =
      modal.querySelector('[data-ic-unlocked-title]');


    const modalFooter =
      modal.querySelector('.ic-pricing-modal__footer');


    const submitButtonHTML =
      submitButton
        ? submitButton.innerHTML
        : '';


    let lastFocusedElement =
      null;



    /* =====================================================
       STORAGE
       ===================================================== */

    const storageKey =
      'stratusIntelliClearPricingRequest';



    /* =====================================================
       PRIVATE-PRICING ACCESS
       The raw token from /checkout lives only here, on the
       store's own origin, and is sent only in POST bodies to
       the app proxy. Never in URLs, Liquid or the page HTML.
       ===================================================== */

    const accessKey =
      'stratusIntelliClearAccess';


    /* Written by the purchase page's access gate so the same
       selection (ids + quantities only) can be requested again. */
    const pendingSelectionKey =
      'stratusIntelliClearPendingSelection';


    function readAccess() {
      try {
        const stored =
          JSON.parse(localStorage.getItem(accessKey) || 'null');


        if (
          stored &&
          typeof stored.token === 'string' &&
          Date.parse(stored.expiresAt) > Date.now()
        ) {
          return stored;
        }


        if (stored) {
          localStorage.removeItem(accessKey);
        }

      } catch (error) {
        /* Storage unavailable */
      }

      return null;
    }


    function saveAccess(access) {
      if (
        !access ||
        typeof access.token !== 'string' ||
        !access.expiresAt
      ) {
        return false;
      }


      try {
        localStorage.setItem(
          accessKey,
          JSON.stringify({
            token: access.token,
            expiresAt: access.expiresAt
          })
        );

        return localStorage.getItem(accessKey) !== null;

      } catch (error) {
        return false;
      }
    }


    function clearAccess() {
      try {
        localStorage.removeItem(accessKey);
      } catch (error) {
        /* Storage unavailable */
      }
    }


    /* { variantId: quantity } from a gated purchase page, if recent. */
    function readPendingSelection() {
      try {
        const stored =
          JSON.parse(sessionStorage.getItem(pendingSelectionKey) || 'null');


        if (
          !stored ||
          typeof stored.items !== 'string' ||
          Date.now() - Number(stored.savedAt) > 60 * 60 * 1000
        ) {
          return null;
        }


        const map = {};

        stored.items.split(',').forEach(function (part) {
          const match =
            /^([0-9]{1,20}):([0-9]{1,2})$/.exec(part);

          if (match) {
            map[match[1]] = Math.min(10, Math.max(1, Number(match[2])));
          }
        });


        return Object.keys(map).length ? map : null;

      } catch (error) {
        return null;
      }
    }


    function clearPendingSelection() {
      try {
        sessionStorage.removeItem(pendingSelectionKey);
      } catch (error) {
        /* Storage unavailable */
      }
    }


    function selectedLines() {
      const pending =
        readPendingSelection() || {};


      return getSelectedVariantIds().map(function (variantId) {
        return {
          variantId: variantId,
          quantity: pending[variantId] || 1
        };
      });
    }



    /* =====================================================
       SELECTION HELPERS
       ===================================================== */

    function getSelectedInputs() {
      return optionInputs.filter(function (input) {
        return input.checked;
      });
    }


    function getSelectedTitles() {
      return getSelectedInputs().map(function (input) {
        return input.value;
      });
    }


    function getSelectedVariantIds() {
      return getSelectedInputs()
        .map(function (input) {
          return input.dataset.icVariantId || '';
        })
        .filter(Boolean);
    }


    function getSelectedProductsForStorage() {
      return getSelectedInputs().map(function (input) {
        return {
          title:
            input.value || '',

          image:
            input.dataset.icImage || '',

          description:
            input.dataset.icDescription || ''
        };
      });
    }


    function getFirstByRole(role) {
      return optionInputs.find(function (input) {
        return input.dataset.icOption === role;
      });
    }


    function getAllByRole(role) {
      return optionInputs.filter(function (input) {
        return input.dataset.icOption === role;
      });
    }



    /* =====================================================
       STORAGE
       ===================================================== */

    function saveRequestState() {
      try {
        const requestData = {
          firstName:
            firstNameInput
              ? firstNameInput.value.trim()
              : '',

          email:
            emailInput
              ? emailInput.value.trim()
              : '',

          items:
            getSelectedTitles(),

          products:
            getSelectedProductsForStorage(),

          variantIds:
            getSelectedVariantIds(),

          timestamp:
            Date.now()
        };


        sessionStorage.setItem(
          storageKey,
          JSON.stringify(requestData)
        );

      } catch (error) {
        /* Enhancement only */
      }
    }


    function getStoredRequest() {
      try {
        const stored =
          sessionStorage.getItem(
            storageKey
          );


        if (!stored) {
          return null;
        }


        return JSON.parse(stored);

      } catch (error) {
        return null;
      }
    }



    /* =====================================================
       HIDDEN FIELDS
       ===================================================== */

    function updateHiddenFields() {
      /* ZIP is required for instant private pricing; optional for "not sure". */
      if (zipInput) {
        zipInput.required =
          isClearSelection();
      }


      const manual =
        getSelectedInputs().some(function (input) {
          return input.dataset.icOption === 'unsure';
        });


      instantCopy.forEach(function (el) {
        el.hidden = manual;
      });


      manualCopy.forEach(function (el) {
        el.hidden = !manual;
      });


      if (requestedItemsField) {
        requestedItemsField.value =
          getSelectedTitles().join(', ');
      }


      if (requestedVariantIdsField) {
        requestedVariantIdsField.value =
          getSelectedVariantIds().join(', ');
      }
    }



    /* =====================================================
       CONTINUE STATE
       ===================================================== */

    function updateContinueState() {
      if (!nextButton) return;


      const hasSelection =
        getSelectedInputs().length > 0;


      nextButton.disabled =
        !hasSelection;


      nextButton.setAttribute(
        'aria-disabled',
        hasSelection
          ? 'false'
          : 'true'
      );
    }



    /* =====================================================
       ERROR
       ===================================================== */

    function clearSelectionError() {
      if (selectionError) {
        selectionError.hidden = true;
      }
    }


    function showSelectionError() {
      if (!selectionError) return;


      selectionError.hidden = false;


      selectionError.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }



    /* =====================================================
       SELECTION RULES
       ===================================================== */

    function handleOptionChange(changedInput) {
      const role =
        changedInput.dataset.icOption;


      const combo =
        getFirstByRole('combo');


      const unsure =
        getFirstByRole('unsure');


      const pcbInputs =
        getAllByRole('pcb');


      const membraneInputs =
        getAllByRole('membrane');


      clearSelectionError();


      /* NOT SURE = EXCLUSIVE */

      if (
        role === 'unsure' &&
        changedInput.checked
      ) {
        optionInputs.forEach(function (input) {
          if (input !== changedInput) {
            input.checked = false;
          }
        });
      }


      /* REAL PRODUCT CLEARS NOT SURE */

      if (
        role !== 'unsure' &&
        changedInput.checked &&
        unsure
      ) {
        unsure.checked = false;
      }


      /* COMBO CLEARS PCB + MEMBRANE */

      if (
        role === 'combo' &&
        changedInput.checked
      ) {
        pcbInputs.forEach(function (input) {
          input.checked = false;
        });


        membraneInputs.forEach(function (input) {
          input.checked = false;
        });
      }


      /* PCB OR MEMBRANE CLEARS COMBO */

      if (
        (
          role === 'pcb' ||
          role === 'membrane'
        ) &&
        changedInput.checked &&
        combo
      ) {
        combo.checked = false;
      }


      /* PCB + MEMBRANE BECOMES COMBO */

      const hasPCB =
        pcbInputs.some(function (input) {
          return input.checked;
        });


      const hasMembrane =
        membraneInputs.some(function (input) {
          return input.checked;
        });


      if (
        hasPCB &&
        hasMembrane &&
        combo
      ) {
        pcbInputs.forEach(function (input) {
          input.checked = false;
        });


        membraneInputs.forEach(function (input) {
          input.checked = false;
        });


        combo.checked = true;
      }


      updateHiddenFields();

      updateContinueState();
    }



    /* =====================================================
       STEP 2 SUMMARY
       ===================================================== */

    function updateSummary() {
      const selected =
        getSelectedInputs();


      if (!selected.length) return;


      if (summaryText) {
        summaryText.textContent =
          'Selected: ' +
          selected
            .map(function (input) {
              return input.value;
            })
            .join(' + ');
      }


      if (summaryImage) {
        const imageInput =
          selected.find(function (input) {
            return input.dataset.icImage;
          });


        if (imageInput) {
          summaryImage.src =
            imageInput.dataset.icImage;


          summaryImage.alt =
            imageInput.value;


          summaryImage.hidden =
            false;

        } else {
          summaryImage.hidden =
            true;
        }
      }
    }



    /* =====================================================
       PANEL ANIMATION
       ===================================================== */

    function animatePanel(panel) {
      if (!panel) return;


      panel.classList.remove(
        'is-entering'
      );


      void panel.offsetWidth;


      panel.classList.add(
        'is-entering'
      );


      window.setTimeout(function () {
        panel.classList.remove(
          'is-entering'
        );
      }, 300);
    }



    /* =====================================================
       STEPS
       ===================================================== */

    function setStep(stepNumber, options) {
      options =
        options || {};


      const isStep1 =
        stepNumber === 1;


      if (step1) {
        step1.hidden =
          !isStep1;
      }


      if (step2) {
        step2.hidden =
          isStep1;
      }


      if (nextButton) {
        nextButton.hidden =
          !isStep1;
      }


      if (submitButton) {
        submitButton.hidden =
          isStep1;
      }


      if (progressFill) {
        progressFill.style.width =
          isStep1
            ? '50%'
            : '100%';
      }


      if (stepCurrent) {
        stepCurrent.textContent =
          String(stepNumber);
      }


      if (modalBody) {
        if (options.instant) {
          modalBody.scrollTop = 0;

        } else {
          modalBody.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      }


      if (isStep1) {
        animatePanel(step1);

      } else {
        updateSummary();

        animatePanel(step2);


        if (
          options.focus !== false
        ) {
          window.setTimeout(function () {
            if (firstNameInput) {
              firstNameInput.focus({
                preventScroll: true
              });
            }
          }, 260);
        }
      }
    }



    /* =====================================================
       OPEN / CLOSE
       ===================================================== */

    function openModal() {
      lastFocusedElement =
        document.activeElement;


      modal.classList.add('is-open');


      modal.setAttribute(
        'aria-hidden',
        'false'
      );


      document.documentElement.classList.add(
        'ic-pricing-modal-open'
      );


      document.body.classList.add(
        'ic-pricing-modal-open'
      );


      if (!successState && !checkoutUrl) {
        setStep(
          1,
          {
            instant: true,
            focus: false
          }
        );


        updateHiddenFields();

        updateContinueState();
      }


      window.requestAnimationFrame(function () {
        const closeButton =
          modal.querySelector(
            '.ic-pricing-modal__close'
          );


        if (closeButton) {
          closeButton.focus({
            preventScroll: true
          });
        }
      });
    }


    function closeModal() {
      modal.classList.remove('is-open');


      modal.setAttribute(
        'aria-hidden',
        'true'
      );


      document.documentElement.classList.remove(
        'ic-pricing-modal-open'
      );


      document.body.classList.remove(
        'ic-pricing-modal-open'
      );


      if (
        lastFocusedElement &&
        typeof lastFocusedElement.focus === 'function'
      ) {
        window.setTimeout(function () {
          lastFocusedElement.focus({
            preventScroll: true
          });
        }, 50);
      }
    }



    /* =====================================================
       EVENTS
       ===================================================== */

    openButton.addEventListener(
      'click',
      openModal
    );


    closeButtons.forEach(function (button) {
      button.addEventListener(
        'click',
        closeModal
      );
    });


    if (backdrop) {
      backdrop.addEventListener(
        'click',
        closeModal
      );
    }


    optionInputs.forEach(function (input) {
      input.addEventListener(
        'change',
        function () {
          handleOptionChange(input);
        }
      );
    });



    /* =====================================================
       CONTINUE
       ===================================================== */

    if (nextButton) {
      nextButton.addEventListener(
        'click',
        function () {

          if (
            !getSelectedInputs().length
          ) {
            showSelectionError();

            return;
          }


          updateHiddenFields();


          if (continueWithExistingAccess()) {
            return;
          }


          setStep(2);
        }
      );
    }



    /* =====================================================
       BACK
       ===================================================== */

    if (backButton) {
      backButton.addEventListener(
        'click',
        function () {
          setStep(1);
        }
      );
    }


    if (changeButton) {
      changeButton.addEventListener(
        'click',
        function () {
          setStep(1);
        }
      );
    }



    /* =====================================================
       SUBMIT
       ===================================================== */

    if (form) {
      form.addEventListener(
        'submit',
        function (event) {

          if (
            !getSelectedInputs().length
          ) {
            event.preventDefault();


            setStep(
              1,
              {
                focus: false
              }
            );


            showSelectionError();


            return;
          }


          updateHiddenFields();


          if (
            !form.checkValidity()
          ) {
            event.preventDefault();


            form.reportValidity();


            return;
          }


          if (
            isClearSelection() &&
            !allowNativeSubmit
          ) {
            event.preventDefault();

            event.stopImmediatePropagation();

            if (!checkoutUrl) {
              saveRequestState();

              requestCheckout();
            }

            return;
          }


          saveRequestState();


          if (submitButton) {
            submitButton.disabled = true;


            submitButton.innerHTML =
              '<span>Sending Request…</span>';
          }
        }
      );
    }



    /* =====================================================
       INSTANT PRIVATE CHECKOUT
       Clear product selections get a Shopify checkout link
       from the app proxy. "I'm not sure what I need" keeps
       the manual-review contact form flow above.
       ===================================================== */

    const checkoutEndpoint =
      '/apps/intelliclear-pricing/checkout';


    const checkoutTimeoutMs =
      20000;


    /* How long "Your private access is ready." shows before the purchase page. */
    const unlockedPauseMs =
      850;


    const phonePattern =
      /^\+?[0-9 ().-]{7,20}$/;


    let checkoutUrl =
      null;


    let checkoutInFlight =
      false;


    /* True only when the manual request flow must post the contact form. */
    let allowNativeSubmit =
      false;


    function isClearSelection() {
      const selected =
        getSelectedInputs();


      return (
        selected.length > 0 &&
        selected.every(function (input) {
          return (
            input.dataset.icOption !== 'unsure' &&
            Boolean(input.dataset.icVariantId)
          );
        })
      );
    }


    function setSubmitLoading(isLoading) {
      if (!submitButton) return;


      submitButton.disabled =
        isLoading;


      submitButton.innerHTML =
        isLoading
          ? '<span>Preparing Checkout…</span>'
          : submitButtonHTML;
    }


    function showCheckoutError(message) {
      if (!checkoutError) return;


      if (message) {
        checkoutError.textContent =
          message;
      }


      checkoutError.hidden =
        false;


      checkoutError.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }


    function hideCheckoutError() {
      if (checkoutError) {
        checkoutError.hidden = true;
      }
    }


    function buildCheckoutPayload() {
      const phone =
        phoneInput
          ? phoneInput.value.trim()
          : '';


      return {
        firstName:
          firstNameInput
            ? firstNameInput.value.trim()
            : '',

        email:
          emailInput
            ? emailInput.value.trim()
            : '',

        /* Phone is optional; never let a loosely typed number block checkout. */
        phone:
          phonePattern.test(phone)
            ? phone
            : '',

        zip:
          zipInput
            ? zipInput.value.trim()
            : '',

        productHandle:
          root.dataset.productHandle || '',

        website:
          honeypotInput
            ? honeypotInput.value
            : '',

        items:
          selectedLines()
      };
    }


    function isSafeCheckoutUrl(value) {
      try {
        const url =
          new URL(value, window.location.origin);


        return (
          url.protocol === 'https:' &&
          url.pathname.indexOf('/cart/') === 0
        );

      } catch (error) {
        return false;
      }
    }


    /* Returning visitor with valid access: confirm it with /status during
       the unlocked pause, then open the purchase page. No form needed.
       If access is no longer valid, fall back to the normal request form. */
    function continueWithExistingAccess() {
      const access =
        readAccess();


      if (!access || !isClearSelection()) {
        return false;
      }


      const purchaseUrl =
        buildPurchaseUrl({
          items: selectedLines()
        });


      if (!purchaseUrl) {
        return false;
      }


      saveRequestState();

      showUnlocked();


      const startedAt =
        Date.now();


      fetch(
        '/apps/intelliclear-pricing/status',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            token: access.token
          })
        }
      )
        .then(function (response) {
          return response
            .json()
            .catch(function () {
              return {};
            })
            .then(function (data) {
              return {
                status: response.status,
                data: data
              };
            });
        })
        .catch(function () {
          return {
            status: 0,
            data: {}
          };
        })
        .then(function (result) {
          const wait =
            Math.max(0, unlockedPauseMs - (Date.now() - startedAt));


          window.setTimeout(function () {
            if (result.data && result.data.active === true) {
              clearPendingSelection();

              window.location.assign(
                purchaseUrl.url
              );

              return;
            }


            /* Expired or revoked on the server: forget it locally. */
            if (result.status === 200) {
              clearAccess();
            }


            hideUnlocked();

            setStep(2);
          }, wait);
        });


      return true;
    }


    function hideUnlocked() {
      if (unlockedState) {
        unlockedState.hidden = true;
      }


      if (modalFooter) {
        modalFooter.style.display = '';
      }


      if (progressRow) {
        progressRow.style.display = '';
      }
    }


    /* "Your private access is ready." — minimal: no products, steps or CTA. */
    function showUnlocked() {
      if (!unlockedState) return;


      if (step1) step1.hidden = true;

      if (step2) step2.hidden = true;

      if (progressRow) progressRow.style.display = 'none';

      if (modalFooter) modalFooter.style.display = 'none';


      unlockedState.hidden =
        false;


      if (modalBody) {
        modalBody.scrollTop = 0;
      }


      if (unlockedTitle) {
        unlockedTitle.focus({
          preventScroll: true
        });
      }
    }


    /* Back button from the purchase page (page restored from the
       back/forward cache): offer the direct checkout instead of a
       stale "preparing" state. */
    window.addEventListener('pageshow', function (event) {
      if (
        !event.persisted ||
        !unlockedState ||
        unlockedState.hidden ||
        !checkoutUrl
      ) {
        return;
      }


      unlockedState.hidden =
        true;


      if (modalFooter) {
        modalFooter.style.display = '';
      }


      showCheckoutReady(
        checkoutUrl
      );
    });


    function showCheckoutReady(url) {
      checkoutUrl =
        url;


      const stored =
        getStoredRequest();


      if (
        checkoutReadyItems &&
        stored &&
        Array.isArray(stored.products)
      ) {
        renderProductReceipt(
          checkoutReadyItems,
          stored.products
        );
      }


      if (step1) step1.hidden = true;

      if (step2) step2.hidden = true;

      if (nextButton) nextButton.hidden = true;

      if (submitButton) submitButton.hidden = true;

      /* The success screen has no progress row; match it. */
      if (progressRow) progressRow.style.display = 'none';


      if (checkoutReady) {
        checkoutReady.hidden = false;
      }


      if (checkoutContinueButton) {
        checkoutContinueButton.hidden = false;
      }


      if (modalBody) {
        modalBody.scrollTop = 0;
      }


      if (checkoutReadyTitle) {
        checkoutReadyTitle.focus({
          preventScroll: true
        });
      }
    }


    /* /pages/private-purchase?items=ID:QTY,...&from=HANDLE
       Variant ids + quantities only (from the validated response); never
       customer details or prices. Null when the theme has no purchase page. */
    function buildPurchaseUrl(data) {
      const base =
        modal.dataset.icPurchaseUrl;


      if (!base || !Array.isArray(data.items) || !data.items.length) {
        return null;
      }


      const items =
        data.items.map(function (item) {
          const id =
            String(item && item.variantId).replace(/^.*\//, '');

          return /^[0-9]{1,20}$/.test(id) && Number.isInteger(item.quantity)
            ? id + ':' + item.quantity
            : null;
        });


      if (items.indexOf(null) !== -1) {
        return null;
      }


      const itemsParam =
        items.join(',');


      const handle =
        root.dataset.productHandle || '';


      let url =
        base + '?items=' + itemsParam;


      if (/^[a-z0-9][a-z0-9-]{0,254}$/.test(handle)) {
        url += '&from=' + handle;
      }


      return {
        url: url,
        items: itemsParam
      };
    }


    /* Lets the purchase page offer this validated checkout link if the
       app is unreachable when it loads. */
    function saveCheckoutFallback(items, url) {
      try {
        sessionStorage.setItem(
          'stratusIntelliClearCheckoutFallback',
          JSON.stringify({
            items: items,
            checkoutUrl: url,
            savedAt: Date.now()
          })
        );

      } catch (error) {
        /* Enhancement only */
      }
    }


    function errorMessageFor(data) {
      const fields =
        data && Array.isArray(data.fields)
          ? data.fields
          : [];


      const hasField = function (name) {
        return fields.some(function (field) {
          return field && field.field === name;
        });
      };


      if (hasField('zip')) {
        return 'Please enter a valid 5-digit U.S. ZIP code.';
      }


      if (hasField('email')) {
        return 'Please check your email address and try again.';
      }


      if (data && data.error === 'rate_limited') {
        return 'We’ve received several requests from you just now. Please try again shortly, or contact us and we’ll help.';
      }


      return 'We couldn’t prepare your checkout right now. Please try again, or contact us and we’ll help.';
    }


    function requestCheckout() {
      if (checkoutInFlight) return;


      checkoutInFlight =
        true;


      hideCheckoutError();

      setSubmitLoading(true);


      const controller =
        typeof AbortController === 'function'
          ? new AbortController()
          : null;


      const timer =
        controller
          ? window.setTimeout(function () {
            controller.abort();
          }, checkoutTimeoutMs)
          : null;


      fetch(
        checkoutEndpoint,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          credentials: 'same-origin',
          body: JSON.stringify(
            buildCheckoutPayload()
          ),
          signal: controller
            ? controller.signal
            : undefined
        }
      )
        .then(function (response) {
          return response
            .json()
            .catch(function () {
              return {};
            });
        })
        .catch(function () {
          return {};
        })
        .then(function (data) {
          if (timer) window.clearTimeout(timer);

          checkoutInFlight = false;


          if (
            data &&
            data.ok === true &&
            isSafeCheckoutUrl(data.checkoutUrl)
          ) {
            const purchaseUrl =
              buildPurchaseUrl(data);


            /* The purchase page needs the access grant; without it
               (storage blocked, or the app couldn't record access)
               the direct checkout screen is the fallback. */
            if (purchaseUrl && saveAccess(data.access)) {
              clearPendingSelection();

              /* Keeps the form guard active while the page changes. */
              checkoutUrl =
                data.checkoutUrl;


              saveCheckoutFallback(
                purchaseUrl.items,
                data.checkoutUrl
              );


              showUnlocked();


              /* A short, deliberate pause on the unlocked state; no click needed. */
              window.setTimeout(function () {
                window.location.assign(
                  purchaseUrl.url
                );
              }, unlockedPauseMs);

              return;
            }


            setSubmitLoading(false);

            showCheckoutReady(
              data.checkoutUrl
            );

            return;
          }


          if (
            data &&
            data.error === 'manual_review_required'
          ) {
            /* The backend wants a person to review this one:
               fall back to the existing manual request flow. */
            setSubmitLoading(false);

            allowNativeSubmit =
              true;


            if (typeof form.requestSubmit === 'function') {
              form.requestSubmit();

            } else {
              nativeFormSubmit.call(form);
            }

            return;
          }


          setSubmitLoading(false);

          showCheckoutError(
            errorMessageFor(data)
          );
        });
    }


    const nativeFormSubmit =
      HTMLFormElement.prototype.submit;


    if (submitButton && form) {
      submitButton.addEventListener(
        'click',
        function (event) {
          if (
            allowNativeSubmit ||
            !isClearSelection()
          ) {
            return;
          }


          event.preventDefault();


          if (checkoutUrl) return;


          updateHiddenFields();


          if (!form.checkValidity()) {
            form.reportValidity();

            return;
          }


          saveRequestState();

          requestCheckout();
        }
      );
    }


    /* No script (including Shopify's contact-form captcha, which calls
       form.submit() after fetching a token) may post the contact form and
       reload the page while a clear selection is on its checkout path. */
    if (form) {
      form.submit = function () {
        if (
          !allowNativeSubmit &&
          (
            checkoutInFlight ||
            checkoutUrl ||
            isClearSelection()
          )
        ) {
          return;
        }


        return nativeFormSubmit.call(form);
      };
    }


    if (checkoutContinueButton) {
      checkoutContinueButton.addEventListener(
        'click',
        function () {
          if (!checkoutUrl) return;


          checkoutContinueButton.disabled =
            true;


          window.location.assign(
            checkoutUrl
          );
        }
      );
    }


    optionInputs.forEach(function (input) {
      input.addEventListener(
        'change',
        hideCheckoutError
      );
    });



    /* =====================================================
       KEYBOARD
       ===================================================== */

    modal.addEventListener(
      'keydown',
      function (event) {

        if (
          event.key === 'Escape'
        ) {
          event.preventDefault();


          closeModal();


          return;
        }


        if (
          event.key !== 'Tab'
        ) {
          return;
        }


        const selectors = [
          'button:not([disabled]):not([hidden])',
          'input:not([disabled]):not([type="hidden"])',
          'select:not([disabled])',
          'textarea:not([disabled])',
          'a[href]'
        ];


        const focusable =
          Array.from(
            modal.querySelectorAll(
              selectors.join(',')
            )
          ).filter(function (element) {
            return (
              element.offsetParent !== null
            );
          });


        if (!focusable.length) return;


        const first =
          focusable[0];


        const last =
          focusable[
            focusable.length - 1
          ];


        if (
          event.shiftKey &&
          document.activeElement === first
        ) {
          event.preventDefault();

          last.focus();
        }


        if (
          !event.shiftKey &&
          document.activeElement === last
        ) {
          event.preventDefault();

          first.focus();
        }
      }
    );



    /* =====================================================
       PERSONALIZED SUCCESS
       ===================================================== */

    function personalizeSuccess() {
      if (!successState) return;


      const storedRequest =
        getStoredRequest();


      if (!storedRequest) return;


      const nameTarget =
        modal.querySelector(
          '[data-ic-success-name]'
        );


      const emailTarget =
        modal.querySelector(
          '[data-ic-success-email]'
        );


      const itemsTarget =
        modal.querySelector(
          '[data-ic-success-items]'
        );


      /* NAME */

      if (
        nameTarget &&
        storedRequest.firstName
      ) {
        nameTarget.textContent =
          ', ' +
          storedRequest.firstName;
      }


      /* EMAIL */

      if (
        emailTarget &&
        storedRequest.email
      ) {
        emailTarget.textContent =
          storedRequest.email;
      }


      /* PRODUCT RECEIPT */

      if (
        itemsTarget &&
        Array.isArray(storedRequest.products) &&
        storedRequest.products.length
      ) {
        renderProductReceipt(
          itemsTarget,
          storedRequest.products
        );
      }
    }


    function renderProductReceipt(itemsTarget, products) {
      if (!products.length) return;


      itemsTarget.innerHTML = '';


      products.forEach(
        function (product) {

          const row =
            document.createElement('div');


          row.className =
            'ic-pricing-success__item';


          const media =
            document.createElement('span');


          media.className =
            'ic-pricing-success__item-media';


          if (product.image) {
            const image =
              document.createElement('img');


            image.className =
              'ic-pricing-success__item-image';


            image.src =
              product.image;


            image.alt =
              product.title || 'Selected IntelliClear product';


            media.appendChild(
              image
            );

          } else {
            const placeholder =
              document.createElement('span');


            placeholder.className =
              'ic-pricing-success__item-placeholder';


            placeholder.textContent =
              '✓';


            media.appendChild(
              placeholder
            );
          }


          const copy =
            document.createElement('span');


          copy.className =
            'ic-pricing-success__item-copy';


          const title =
            document.createElement('strong');


          title.textContent =
            product.title;


          copy.appendChild(
            title
          );


          if (product.description) {
            const description =
              document.createElement('small');


            description.textContent =
              product.description;


            copy.appendChild(
              description
            );
          }


          row.appendChild(
            media
          );


          row.appendChild(
            copy
          );


          itemsTarget.appendChild(
            row
          );
        }
      );
    }



    /* =====================================================
       RESTORE ERROR STATE
       ===================================================== */

    /* Selection carried over from a gated purchase page (variant ids only). */
    function applyPendingSelection() {
      const pending =
        readPendingSelection();


      if (!pending) return false;


      let matched =
        false;


      optionInputs.forEach(function (input) {
        const isPending =
          Boolean(input.dataset.icVariantId && pending[input.dataset.icVariantId]);

        input.checked =
          isPending;

        matched =
          matched || isPending;
      });


      updateHiddenFields();

      updateContinueState();

      return matched;
    }


    function restoreStoredSelections() {
      const storedRequest =
        getStoredRequest();


      if (
        !storedRequest ||
        !Array.isArray(storedRequest.items)
      ) {
        return;
      }


      optionInputs.forEach(function (input) {
        input.checked =
          storedRequest.items.includes(
            input.value
          );
      });


      updateHiddenFields();

      updateContinueState();
    }



    /* =====================================================
       SHOPIFY RETURN STATES
       ===================================================== */

    if (successState) {
      personalizeSuccess();


      modal.classList.add('is-open');


      modal.setAttribute(
        'aria-hidden',
        'false'
      );


      document.documentElement.classList.add(
        'ic-pricing-modal-open'
      );


      document.body.classList.add(
        'ic-pricing-modal-open'
      );


      if (modalBody) {
        modalBody.scrollTop = 0;
      }
    }


    if (formErrorState) {
      restoreStoredSelections();


      modal.classList.add('is-open');


      modal.setAttribute(
        'aria-hidden',
        'false'
      );


      document.documentElement.classList.add(
        'ic-pricing-modal-open'
      );


      document.body.classList.add(
        'ic-pricing-modal-open'
      );


      setStep(
        2,
        {
          instant: true,
          focus: false
        }
      );
    }



    /* =====================================================
       RETURN FROM THE PRIVATE PURCHASE PAGE
       "Edit selections" links to #intelliclear-pricing
       ===================================================== */

    if (
      !successState &&
      !formErrorState &&
      !autoOpened &&
      window.location.hash === '#intelliclear-pricing'
    ) {
      autoOpened = true;

      if (!applyPendingSelection()) {
        restoreStoredSelections();
      }

      openModal();
    }



    /* =====================================================
       INITIAL
       ===================================================== */

    updateHiddenFields();

    updateContinueState();
  }


  let autoOpened =
    false;



  /* =========================================================
     INIT ALL
     ========================================================= */

  function initAllPricingSections() {
    document
      .querySelectorAll(
        '[data-ic-pricing-request]'
      )
      .forEach(
        initIntelliClearPricing
      );
  }


  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initAllPricingSections
    );

  } else {
    initAllPricingSections();
  }


  document.addEventListener(
    'shopify:section:load',
    function (event) {

      event.target
        .querySelectorAll(
          '[data-ic-pricing-request]'
        )
        .forEach(
          initIntelliClearPricing
        );

    }
  );

})();